import i18n from 'i18next';

import { InputTypes, PaymentFormField } from '@cashier/cashier-model';

interface ValidationReport {
  isValid: boolean;
  errorMessage?: string;
}

type InputTypeValidator = Record<
  InputTypes,
  (field: PaymentFormField) => ValidationReport
>;

export const CommonValidatorMap: Partial<InputTypeValidator> = {
  [InputTypes.Integer]: (field) => {
    const { value, params } = field;
    const { min, max } = params || {};

    if (!/^\d+$/.test(value as string)) return { isValid: false };

    if (!min && !max) return { isValid: true };

    if (min && !max) {
      return { isValid: Number(value) >= Number(min) };
    }

    if (max && !min) {
      return { isValid: Number(value) <= Number(max) };
    }

    return {
      isValid: Number(value) >= Number(min) && Number(value) <= Number(max),
    };
  },
  [InputTypes.IdentificationNumber]: (field) => {
    if (/^[0-9-]{8,11}$/.test(field.value as string)) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('rucIdValidationFailed.message'),
    };
  },
  [InputTypes.PayzenPayTmAccountNumber]: (field) => {
    const value = field.value as string;

    return { isValid: /^\d+$/.test(value) && value.length === 10 };
  },
  [InputTypes.PayzenImpsAccountNumber]: (field) => {
    const value = field.value as string;
    return {
      isValid: /^\d+$/.test(value) && value.length >= 10 && value.length <= 16,
    };
  },
  [InputTypes.Email]: (field) => {
    if (
      // eslint-disable-next-line max-len,no-control-regex
      /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i.test(
        field.value as string,
      )
    ) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('emailValidationFailed.message'),
    };
  },
  [InputTypes.Number]: (field) => {
    const { value, params } = field;
    const { min, max } = params || {};

    if (!min && !max) return { isValid: true };

    if (min && !max) {
      return { isValid: Number(value) >= Number(min) };
    }

    if (max && !min) {
      return { isValid: Number(value) <= Number(max) };
    }

    return {
      isValid: Number(value) >= Number(min) && Number(value) <= Number(max),
    };
  },
  [InputTypes.CreditCardNumber]: (field) => {
    const value = field.value as string;

    if (value.length <= 23 && value.length >= 19) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('cardNumberValidationFailed.message'),
    };
  },
  [InputTypes.CreditCardVisa]: (field) => {
    if (/^4\d{3}([ -]?)\d{4}\1\d{4}\1\d{4}$/.test(field.value as string)) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('cardNumberValidationFailed.message'),
    };
  },
  [InputTypes.CreditCardMastercard]: (field) => {
    if (/^5[1-5]\d{2}([ -]?)\d{4}\1\d{4}\1\d{4}$/.test(field.value as string)) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('cardNumberValidationFailed.message'),
    };
  },
  [InputTypes.Cvv]: (field) => {
    if (/^[0-9]{3,4}$/.test(field.value as string)) {
      return { isValid: true };
    }

    return {
      isValid: false,
      errorMessage: i18n.t('cvvValidationFailed.message'),
    };
  },
  [InputTypes.DateTime]: (field) => {
    const value = field.value as string;

    if (!value.includes('_')) {
      const month = value.substring(0, 2);
      const yearPart = value.substring(3, 7);
      const fullYear = yearPart.length === 2 ? `20${yearPart}` : yearPart;
      const dateString = `${fullYear}-${month}-01`;

      const expiryDate = new Date(dateString);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      if (expiryDate >= currentDate) {
        return { isValid: true };
      }

      return {
        isValid: false,
        errorMessage: i18n.t('dateFormatValidationFailed.message'),
      };
    }

    return { isValid: false };
  },
  [InputTypes.Input]: (field) => {
    const value = field.value as string;

    if (!value || value.trim() === '') {
      return { isValid: false };
    }

    if (field.name === 'cardHolder') {
      return { isValid: /^[a-zA-Z\s]{3,50}$/.test(value.trim()) };
    }

    return { isValid: true };
  },
  [InputTypes.Text]: (field) => {
    const value = field.value as string;

    return { isValid: !!value && value.trim() !== '' };
  },
  [InputTypes.Checkbox]: (field) => {
    return { isValid: field.value === true };
  },
  [InputTypes.Hidden]: (_) => {
    return { isValid: true };
  },
};
